<template>
  <div>
    <b-modal
      :id="MODAL_ID"
      title="Translate Image"
      size="lg"
      hide-footer
      no-close-on-backdrop
      modal-class="translate-template-image-modal"
      header-class="draggable-modal-header border-0"
      body-class="py-0"
      @show="onModalOpen"
      @shown="onModalShown"
      @hide="onModalHide"
      @hidden="onModalHidden"
    >
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center w-100">
          <h5 class="modal-title mb-0 text-lg font-semibold">
            Translate Image
          </h5>
          <div class="ml-auto d-flex align-items-center">
            <b-dropdown
              right
              no-caret
              variant="transparent"
              class="translate-advanced-dropdown"
              toggle-class="btn-sm translate-settings-gear"
              menu-class="translate-advanced-menu"
            >
              <template #button-content>
                <i
                  v-b-tooltip.hover.viewport.noninteractive
                  class="bi bi-gear"
                  title="Advanced"
                />
              </template>
              <div class="translate-advanced-menu__title">
                Advanced
              </div>
              <div class="form-group mb-0">
                <label
                  for="modelSelect"
                  class="font-medium text-sm"
                >Select Model</label>
                <select
                  id="modelSelect"
                  v-model="selectedModel"
                  class="form-control custom-select"
                >
                  <option
                    v-for="opt in modelOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}
                  </option>
                </select>
                <div class="custom-control custom-checkbox mt-2">
                  <input
                    id="upscaleTranslatedImageCheckbox"
                    v-model="upscaleTranslatedImage"
                    type="checkbox"
                    class="custom-control-input"
                  >
                  <label
                    class="custom-control-label"
                    for="upscaleTranslatedImageCheckbox"
                  >
                    Allow the translated image to be upscaled
                    <span
                      class="upscale-image-tooltip"
                      role="tooltip"
                    >
                      Caution: Upscaling may inaccurately reconstruct or invent small text that is unreadable in the original image.
                    </span>
                  </label>
                </div>
              </div>
            </b-dropdown>
            <button
              type="button"
              class="close"
              aria-label="Close"
              @click="close"
            >
              <i class="bi bi-x" />
            </button>
          </div>
        </div>
      </template>
      <div class="position-relative modal-content-wrapper">
        <div v-if="!showTranslationSettings">
          <div
            v-if="isLoading"
            class="translate-running-banner"
          >
            <i class="bi bi-info-circle-fill translate-running-banner__icon" />
            <span>You can close this window. Translation will continue and we’ll notify you when it’s done.</span>
          </div>
          <div class="form-group">
            <label
              class="font-medium text-sm"
              for="backgroundImageSelect"
            >Select Image</label>
            <b-dropdown
              id="backgroundImageSelect"
              :text="selectedImageText"
              block
              menu-class="w-100 image-dropdown-menu"
              variant="outline-secondary"
              class="image-select-dropdown"
            >
              <b-dropdown-item
                v-for="option in backgroundImages"
                :key="option.value"
                :active="selectedBackgroundImageIndex === option.value"
                @click="selectedBackgroundImageIndex = option.value"
              >
                <div class="d-flex align-items-center">
                  <img
                    v-if="option.thumbnail"
                    :src="option.thumbnail"
                    class="image-thumbnail mr-2"
                    alt=""
                  >
                  <span
                    v-else
                    class="image-thumbnail-placeholder mr-2"
                  />
                  <span>{{ option.text }}</span>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div v-if="selectedImage">
            <div class="d-flex justify-content-between align-items-center">
              <label class="font-medium text-sm">
                Select Text Blocks to Translate
              </label>
              <button
                type="button"
                class="btn btn-link p-0 clear-selection-button"
                :disabled="isLoadingTextBlocks || !hasSelectedTextBlocks"
                @click="clearSelectedTextBlocks"
              >
                Clear
              </button>
            </div>
            <div class="form-group">
              <Multiselect
                v-model="selectedTextBlocks"
                :options="isLoadingTextBlocks ? [] : detectedTextBlocks"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :placeholder="isLoadingTextBlocks ? 'Loading...' : 'Select text blocks'"
                :disabled="isLoadingTextBlocks"
                @input="showTextBlocksError = false"
              >
                <template #option="{ option }">
                  <span>{{ option }}</span>
                  <span
                    v-if="isProductImageTextBlock(option)"
                    class="text-block-product-badge"
                  >
                    Product image
                  </span>
                </template>
              </Multiselect>
              <div
                v-if="showTextBlocksError"
                class="text-danger small mt-1"
              >
                Please select at least one text block to translate.
              </div>
              <div class="custom-control custom-checkbox mt-2">
                <input
                  id="detectBrandTextCheckbox"
                  v-model="detectBrandText"
                  type="checkbox"
                  class="custom-control-input"
                >
                <label
                  class="custom-control-label"
                  for="detectBrandTextCheckbox"
                >
                  Include logos, brand names, and product names
                </label>
              </div>
            </div>
            <div class="form-group">
              <div class="d-flex justify-content-between align-items-center">
                <label
                  for="targetLanguageSelect"
                  class="font-medium text-sm"
                >
                  Select Languages
                </label>
                <div class="d-flex align-items-center language-actions">
                  <button
                    type="button"
                    class="btn btn-link p-0 clear-selection-button glossary-settings-button"
                    @click="openTranslationSettings"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="glossary-settings-button__icon"
                      aria-hidden="true"
                    >
                      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
                      <path d="m8 13 4-7 4 7" />
                      <path d="M9.1 11h5.7" />
                    </svg>
                    Glossary &amp; excluded terms
                  </button>
                  <button
                    type="button"
                    class="btn btn-link p-0 clear-selection-button"
                    :disabled="!canTranslate"
                    @click="clearSelectedTargetLanguages"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <Multiselect
                id="targetLanguageSelect"
                v-model="selectedTargetLanguages"
                :options="availableLanguages"
                :multiple="true"
                :close-on-select="false"
                :taggable="false"
                placeholder="Select language(s)..."
                class="multiselect-language"
              />
            </div>
            <b-alert
              variant="warning"
              dismissible
              :show="showAlreadyGeneratedWarning"
              class="mt-3"
              @dismissed="showAlreadyGeneratedWarning = false"
            >
              All selected languages have already been generated.
            </b-alert>
            <div
              v-if="selectedImage"
              :class="{ 'translated-images-grid': translatedImageUrls.length > 0 }"
            >
              <!-- Original Image -->
              <div class="translated-image-item">
                <div class="image-preview-wrapper position-relative">
                  <span
                    v-if="editedImageUrl || translatedImageUrls.length > 0"
                    class="translated-image-label text-xs text-muted-foreground"
                  >
                    Original
                  </span>

                  <img
                    class="img img-fluid modal-preview-image"
                    :src="selectedImage"
                  >

                  <div
                    class="image-actions-container"
                    :style="editedImageUrl || translatedImageUrls.length > 0 ? { top: '35px' } : { top: '10px' }"
                  >
                    <button
                      class="download-btn"
                      title="Download image"
                      @click.stop="toggleOriginalDownloadOptions"
                    >
                      <i class="bi bi-download" />
                    </button>

                    <div
                      v-if="activeOriginalDownloadOptions"
                      class="custom-download-dropdown"
                      @click.stop
                    >
                      <div class="d-flex align-items-center mb-3">
                        <label class="custom-radio mr-3 mb-0">
                          <input
                            v-model="downloadOptions.format"
                            type="radio"
                            value="jpg"
                          >
                          <span /> JPG
                        </label>
                        <label class="custom-radio mb-0">
                          <input
                            v-model="downloadOptions.format"
                            type="radio"
                            value="png"
                          >
                          <span /> PNG
                        </label>
                      </div>

                      <div
                        v-if="downloadOptions.format === 'jpg'"
                        class="d-flex align-items-center mb-3"
                      >
                        <label class="download-label mb-0 mr-2">Max size</label>
                        <input
                          v-model.number="downloadOptions.maxSize"
                          type="number"
                          class="form-control form-control-sm download-size-input"
                        >
                        <span class="download-unit ml-2">KB</span>
                      </div>

                      <button
                        :disabled="isDownloading"
                        type="button"
                        class="btn btn-primary w-100"
                        @click.stop="downloadImage(selectedImage, 'original', {
                          format: downloadOptions.format,
                          maxSizeKB: downloadOptions.maxSize,
                        })"
                      >
                        <i class="bi bi-download mr-2" />
                        {{ isDownloading ? "Preparing download..." : `Download ${downloadOptions.format.toUpperCase()}` }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Translated Images -->
              <div
                v-for="(item, index) in translatedImageUrls"
                :key="index"
                class="translated-image-item"
              >
                <div class="image-preview-wrapper position-relative">
                  <span
                    v-if="translatedImageUrls.length > 0"
                    class="translated-image-label text-xs text-muted-foreground"
                  >
                    {{ item.language || `Image ${index + 1}` }}
                  </span>

                  <!-- Loading State -->
                  <div
                    v-if="item.isLoading"
                    class="d-flex flex-column justify-content-center align-items-center bg-light"
                    style="min-height: 200px; width: 100%; padding-left: 16px; padding-right: 16px; border-radius: 4px;"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="sr-only">Loading...</span>
                    </div>
                    <div class="mt-2 text-muted small">
                      Translating...
                    </div>
                  </div>

                  <!-- Error State -->
                  <div
                    v-else-if="item.error"
                    class="d-flex flex-column justify-content-center align-items-center bg-light text-danger"
                    style="min-height: 200px; width: 100%; border-radius: 4px;"
                  >
                    <i
                      class="bi bi-exclamation-triangle mb-2"
                      style="font-size: 2rem;"
                    />
                    <span
                      class="text-center px-3"
                      :class="item.errorMessage ? 'mb-1' : 'mb-2'"
                    >
                      {{ item.errorMessage ? "Translation blocked by OpenAI safety filters" : "Translation Failed" }}
                    </span>
                    <span
                      v-if="item.errorMessage"
                      class="mb-2 text-center px-3"
                    >
                      {{ item.errorMessage }}
                    </span>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click.stop="retryTranslation(index)"
                    >
                      <i class="bi bi-arrow-clockwise mr-1" /> Retry
                    </button>
                  </div>

                  <!-- Success State -->
                  <template v-else-if="item.url">
                    <img
                      class="img img-fluid modal-preview-image"
                      :src="item.url"
                    >

                    <div
                      class="image-actions-container"
                      :style="translatedImageUrls.length > 0 ? { top: '35px' } : { top: '10px' }"
                    >
                      <button
                        class="retry-btn"
                        title="Retry translation"
                        @click.stop="retryTranslation(index)"
                      >
                        <i class="bi bi-arrow-clockwise" />
                      </button>
                      <button
                        class="download-btn"
                        title="Download image"
                        @click.stop="toggleDownloadOptions(index)"
                      >
                        <i class="bi bi-download" />
                      </button>

                      <div
                        v-if="activeDownloadOptionsIndex === index"
                        class="custom-download-dropdown"
                        @click.stop
                      >
                        <div class="d-flex align-items-center mb-3">
                          <label class="custom-radio mr-3 mb-0">
                            <input
                              v-model="downloadOptions.format"
                              type="radio"
                              value="jpg"
                            >
                            <span /> JPG
                          </label>

                          <label class="custom-radio mb-0">
                            <input
                              v-model="downloadOptions.format"
                              type="radio"
                              value="png"
                            >
                            <span /> PNG
                          </label>
                        </div>

                        <div 
                          v-if="downloadOptions.format === 'jpg'"
                          class="d-flex align-items-center mb-3"
                        >
                          <label class="download-label mb-0 mr-2">
                            Max size
                          </label>

                          <input
                            v-model.number="downloadOptions.maxSize"
                            type="number"
                            class="form-control form-control-sm download-size-input"
                          >

                          <span class="download-unit ml-2">KB</span>
                        </div>

                        <button
                          :disabled="isDownloading"
                          type="button"
                          class="btn btn-primary w-100"
                          @click.stop="downloadImage(item.url, item.language, {
                            format: downloadOptions.format,
                            maxSizeKB: downloadOptions.maxSize,
                          })"
                        >
                          <i class="bi bi-download mr-2" />
                          {{ isDownloading ? "Preparing download..." : `Download ${downloadOptions.format.toUpperCase()}` }}
                        </button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="text-removal-footer d-flex flex-column gap-2">
              <div 
                class="d-flex flex-row justify-content-between gap-2"
                style="padding: 16px 0px;"
              >
                <div class="d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="isLoading || isCreatingTemplate || isRemovingBackground || !canTranslate || isLoadingTextBlocks"
                    @click="handleTranslate()"
                  >
                    <span
                      v-if="isLoading"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <b-icon icon="translate" />
                    {{ isLoading ? "Translating..." : "Translate" }}
                  </button>
                  <button
                    v-if="editedImageUrl"
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="isLoading || isCreatingTemplate || isRemovingBackground"
                    @click="handleRemoveBackground()"
                  >
                    <span
                      v-if="isRemovingBackground"
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <i
                      v-else
                      class="bi bi-image"
                    />
                    {{ isRemovingBackground ? "Removing..." : "Remove Background" }}
                  </button>
                </div>
                <div
                  v-if="editedImageUrl"
                  class="d-flex gap-2"
                >
                  <div
                    v-if="hasMultipleLanguages"
                    class="position-relative"
                  >
                    <button
                      type="button"
                      class="btn btn-primary"
                      :disabled="isCreatingTemplate || isDownloadingAllImages || isAnyImageProcessing()"
                      @click.stop="handleDownloadAllOptions"
                    >
                      <span
                        v-if="isDownloadingAllImages"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span v-if="isDownloadingAllImages">Downloading...</span>
                      <span v-if="!isDownloadingAllImages"><i class="bi bi-download mr-1" /> Download All <i class="bi bi-chevron-down ml-1" /></span>
                    </button>

                    <div
                      v-if="activeDownloadAllOptions"
                      class="custom-download-dropdown custom-download-dropdown--download-all"
                      @click.stop
                    >
                      <div class="d-flex align-items-center mb-3">
                        <label class="custom-radio mr-3 mb-0">
                          <input
                            v-model="downloadOptions.format"
                            type="radio"
                            value="jpg"
                          >
                          <span /> JPG
                        </label>
                        <label class="custom-radio mb-0">
                          <input
                            v-model="downloadOptions.format"
                            type="radio"
                            value="png"
                          >
                          <span /> PNG
                        </label>
                      </div>

                      <div
                        v-if="downloadOptions.format === 'jpg'"
                        class="d-flex align-items-center mb-3"
                      >
                        <label class="download-label mb-0 mr-2">Max size</label>
                        <input
                          v-model.number="downloadOptions.maxSize"
                          type="number"
                          class="form-control form-control-sm download-size-input"
                        >
                        <span class="download-unit ml-2">KB</span>
                      </div>

                      <button
                        :disabled="isDownloadingAllImages"
                        type="button"
                        class="btn btn-primary w-100"
                        @click.stop="downloadAllImagesZip({
                          format: downloadOptions.format,
                          maxSizeKB: downloadOptions.maxSize,
                        })"
                      >
                        <i class="bi bi-download mr-2" />
                        {{ isDownloadingAllImages ? "Preparing download..." : `Download All ${downloadOptions.format.toUpperCase()}` }}
                      </button>
                    </div>
                  </div>
                  <b-dropdown
                    text="Add to New Template"
                    variant="primary"
                    :disabled="isCreatingTemplate || isAnyImageProcessing()"
                  >
                    <b-dropdown-item @click="handleAddToNewTemplate('original')">
                      Original size
                    </b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-item @click="handleAddToNewTemplate(1500, 1500)">
                      1500x1500
                    </b-dropdown-item>
                    <b-dropdown-item @click="handleAddToNewTemplate(2000, 2000)">
                      2000x2000
                    </b-dropdown-item>
                    <b-dropdown-item @click="handleAddToNewTemplate(2500, 2500)">
                      2500x2500
                    </b-dropdown-item>
                    <b-dropdown-item @click="handleAddToNewTemplate(3000, 3000)">
                      3000x3000
                    </b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-item @click="handleAddToNewTemplate('custom')">
                      Custom...
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-dropdown
                    v-if="!hasMultipleLanguages"
                    split
                    variant="primary"
                    :disabled="isCreatingTemplate"
                    @click="handleSave(false)"
                  >
                    <template #button-content>
                      <span
                        v-if="isCreatingTemplate"
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      {{ isCreatingTemplate ? "Creating..." : "Add to Template" }}
                    </template>
                    <b-dropdown-item
                      :disabled="isProductImage"
                      @click="handleSave(true)"
                    >
                      {{ isCreatingTemplate ? "Creating..." : "Add to template & open Make Editable modal" }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <button
            type="button"
            class="btn btn-link p-0 mb-3"
            @click="handleBackToTranslate"
          >
            <i class="bi bi-arrow-left-short mr-1" /> Back to Translate
          </button>
          <TranslationSettingsView
            mode="modal"
            :show-header-description="true"
            @settings-changed="applyTranslationSettings"
          />
        </div>

        <div 
          v-if="isLoadingTextBlocks" 
          class="loading-overlay"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 text-white font-weight-bold">
            Detecting text blocks...
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2";
import TranslationSettingsView from "@frontend/components/common/translation-settings/TranslationSettingsView.vue";
import API_TEMPLATE from "@frontend/services/api/template";
import {
    translateTemplateImageEventBroker,
    EVENTS as MODAL_EVENTS,
} from "@frontend/group/modules/translate-template-image/event-broker";
import {
    FIELD_TYPE_BACKGROND_THEME_IMAGE,
    FIELD_TYPE_PRODUCT_IMAGE,
    FIELD_TYPE_STATIC_IMAGE,
    FIELD_TYPE_UPLOAD_IMAGE,
    FIELD_TYPE_IMAGE_FROM_BACKGROUND,
} from "@frontend/constants/type-fields-of-template";
import {
    addAddField,
    DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES,
} from "@frontend/group/modules/additional-fields";
import {
    editorControlEventBroker,
    EVENTS,
} from "@frontend/group/modules/editor-control-popup/event-broker";
import { openMakeTemplateImageEditableForInstance } from "@frontend/group/modules/make-template-image-editable";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from "@frontend/constants/global-update";
import { deleteURLParameters, setURLParameters } from "@frontend/utils";
import parseText from "@/common/preview/parse-text";
import { canvasEditModeControl } from "@frontend/group/modules/canvas-edit-mode-control";
import Requests from "@frontend/services/api/file-browser";
import { uploadFolderFile, uploadFile } from "@frontend/services/file-uploader";
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import Multiselect from "vue-multiselect";
import JSZip from 'jszip';
import { loadTranslationSettings } from "@frontend/components/common/translation-settings/translation-settings-storage";

const getShowPageScope = () => ShowPageScopeCollector.get(['replaceBackgroundImage']);

const MODAL_ID = "translate-template-image-modal";
const TRANSLATION_SETTINGS_MODAL_ID = "translation-settings-modal";

export default {
    name: "TranslateTemplateImageModal",
    components: {
        Multiselect,
        TranslationSettingsView,
    },
    data() {
        return {
            MODAL_ID,
            isOpen: false,
            instanceId: null,
            backgroundImages: [],
            selectedBackgroundImageIndex: "none",
            availableLanguages: [
                "Arabic",
                "Bengali",
                "Cantonese",
                "Czech",
                "Dutch",
                "English",
                "French-CA",
                "French-FR",
                "German",
                "Greek",
                "Hebrew",
                "Hindi",
                "Italian",
                "Japanese",
                "Korean",
                "Polish",
                "Portuguese",
                "Russian",
                "Simplified Chinese",
                "Slovak",
                "Spanish (LatAm)",
                "Spanish (Spain/EU)",
                "Swedish",
                "Thai",
                "Traditional Chinese",
                "Turkish",
                "Vietnamese",
            ],
            selectedTargetLanguages: [],
            fields: [],
            objects: [],
            targetElement: {},
            selectedImage: null,
            removeTextFromImage: true,
            isLoading: false,
            isCreatingTemplate: false,
            isRemovingBackground: false,
            editedImageUrl: null,
            translatedImageUrls: [],
            translationsByKey: {},
            textBlocksByKey: {},
            layoutId: 0,
            folderId: 0,
            isLoadingTextBlocks: false,
            rawDetectedTextBlocks: [],
            detectedTextBlocks: [],
            selectedTextBlocks: [],
            showTextBlocksError: false,
            detectBrandText: false,
            imageTranslationModel: "gemini_nano_banana_pro_image_generate",
            imageTranslationQuality: "2k",
            projectName: null,
            isDownloadingAllImages: false,
            shouldOpenMakeEditableAfterHidden: false,
            glossary: [],
            exclude: [],
            showTranslationSettings: false,
            modalDialogElement: null,
            modalHeaderElement: null,
            isDraggingModal: false,
            modalDragStartX: 0,
            modalDragStartY: 0,
            modalTranslateX: 0,
            modalTranslateY: 0,
            modalStartTranslateX: 0,
            modalStartTranslateY: 0,
            activeDownloadOptionsIndex: null,
            downloadOptions: {
              format: "jpg",
              maxSize: 1500,
            },
            isDownloading: false,
            activeOriginalDownloadOptions: false,
            activeDownloadAllOptions: false,
            selectedModel: "gemini_nano_banana_pro",
            upscaleTranslatedImage: false,
            modelOptions: [
                { value: "gemini_nano_banana_pro", text: "Gemini / Nano Banana (Faster)" },
                { value: "gpt-image-2", text: "GPT-Image-2 (Slower, best quality)" }
            ],
            showAlreadyGeneratedWarning: false,
        };
    },
    computed: {
        ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
        selectedImageText() {
            const selected = this.backgroundImages.find(
                (opt) => opt.value === this.selectedBackgroundImageIndex,
            );
            return selected ? selected.text : "Select Image";
        },
        isDefaultImage() {
            return this.selectedImage?.includes(
                DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES[
                    "additional_background_images"
                ],
            );
        },
        shouldShowSizeWarning() {
            // Calculate original dimensions based on scale
            const originalWidth = this.imageSelection.width * this.scaleX;
            const originalHeight = this.imageSelection.height * this.scaleY;
            return originalWidth > 1024 || originalHeight > 1024;
        },
        hasAnySelection() {
            return this.selections.length > 0 || this.hasSelection;
        },
        selectedFieldType() {
            const selected = this.backgroundImages.find(
                (opt) => opt.value === this.selectedBackgroundImageIndex,
            );
            return selected?.fieldType || null;
        },
        isProductImage() {
            return this.selectedFieldType === FIELD_TYPE_PRODUCT_IMAGE;
        },
        isSelectedCanvasProductImage() {
            const elementId = String(
                this.targetElement?.element_id ||
                this.targetElement?.templateField?.element_id ||
                this.targetElement?.id ||
                "",
            );

            return this.selectedFieldType === FIELD_TYPE_PRODUCT_IMAGE ||
                elementId.includes("product_image");
        },
        hasMultipleLanguages() {
            return this.selectedTargetLanguages.length > 1;
        },
        canTranslate() {
            return this.selectedTargetLanguages.length > 0;
        },
        hasSelectedTextBlocks() {
            return this.selectedTextBlocks.length > 0;
        },
        instanceTemplateName() {
            return this.selectedInstance?.template?.name || null;
        },
    },
    watch: {
        selectedBackgroundImageIndex() {
            if (this.selectedBackgroundImageIndex === "none") {
                return;
            }
            this.targetElement =
                this.objects[this.selectedBackgroundImageIndex].target;
            this.refreshImage();
        },
        selectedImage(newVal) {
            if (newVal) {
                // If we already detected text blocks for this image (e.g. reopening the
                // popup while translations are in progress), reuse them instead of
                // re-running detection — avoids the blocking "Detecting text blocks…"
                // overlay covering the in-progress previews.
                const cached = this.cachedTextBlocks();
                if (cached) {
                    this.rawDetectedTextBlocks = cached.raw.map((b) => ({ ...b }));
                    this.detectedTextBlocks = [...cached.detected];
                    this.selectedTextBlocks = [...cached.selected];
                } else {
                    this.fetchTextLinesFromImage();
                }
            } else {
                this.rawDetectedTextBlocks = [];
                this.detectedTextBlocks = [];
                this.selectedTextBlocks = [];
                this.editedImageUrl = null;
            }
        },
        detectBrandText(newVal) {
            if (this.isSelectedCanvasProductImage) return;

            if (newVal) {
                this.selectedTextBlocks = [...this.detectedTextBlocks];
            } else {
                this.selectedTextBlocks = this.rawDetectedTextBlocks
                    .filter(b => !b.isLogoProductNameOrBrandName)
                    .map(b => b.text);
            }
        },
        selectedTargetLanguages(newVal) {
            if (!this.translatedImageUrls) return;
            
            // Remove previews of languages that have been deselected
            this.translatedImageUrls = this.translatedImageUrls.filter(item => 
                newVal.includes(item.language)
            );
        },
    },
    mounted() {
        document.addEventListener("click", this.closeDownloadOptions);
        translateTemplateImageEventBroker.on(
            MODAL_EVENTS.INIT,
            this.init.bind(this),
        );
        translateTemplateImageEventBroker.on(
            MODAL_EVENTS.CLOSED,
            this.onClose.bind(this),
        );
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeDownloadOptions);
        this.removeModalDragListeners();
    },
    methods: {
        openTranslationSettings() {
            this.showTranslationSettings = true;
        },
        handleBackToTranslate() {
            this.showTranslationSettings = false;
            this.updateTranslationSettings();
        },
        applyTranslationSettings(settings) {
            this.glossary = settings?.glossary || [];
            this.exclude = settings?.exclude || [];
        },
        async callOpenAI(settings) {
            const { data } = await axios.post(`/call-open-ai`, settings);
            if (data.error) {
              throw new Error(data.error.message);
            }
            return data;
        },
        init({ fields, preview, objects, targetLanguages = [], translateSection = 0 }) {
            this.instanceId = preview.id;
            this.fields = fields;
            this.objects = objects;
            if (translateSection) {
              this.selectedTargetLanguages = [...targetLanguages];
            }
            this.projectName = preview.data.project_name || null;
            this.upscaleTranslatedImage = false;
            

            
            this.backgroundImages = [
                {
                    text: "Select Image",
                    value: "none",
                    thumbnail: null,
                },
            ];
            this.selectedBackgroundImageIndex = "none";

            const url = window.location.href;
            const match = url.match(/\/group\/(\d+)\//);

            if (match) {
                this.layoutId = parseInt(match[1], 10);
            }

            const imageFieldTypes = [
                FIELD_TYPE_BACKGROND_THEME_IMAGE,
                FIELD_TYPE_PRODUCT_IMAGE,
                FIELD_TYPE_STATIC_IMAGE,
                FIELD_TYPE_UPLOAD_IMAGE,
                FIELD_TYPE_IMAGE_FROM_BACKGROUND,
            ];

            for (let i = 0; i < this.fields.length; i++) {
                const field = this.fields[i];
                // Check if it's an image field type
                const isImageField = imageFieldTypes.includes(field.type);
                const urlString = field.url?.href || field.url?.toString?.() || field.url;
                const isDefaultImage = urlString === DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images;

                if (isImageField && !isDefaultImage) {
                    // Get thumbnail URL from field (already prepared in index.js)
                    const thumbnailUrl = field.thumbnail?.href || field.thumbnail?.toString?.() || field.thumbnail;

                    this.backgroundImages.push({
                        text: field.name,
                        value: i,
                        thumbnail: typeof thumbnailUrl === "string" ? thumbnailUrl : null,
                        fieldType: field.type,
                        projectName: this.projectName,
                    });

                    if (field.id === preview.canvas.getActiveObject()?.id) {
                        this.selectedBackgroundImageIndex = i;
                    }
                }
            }

            // If there's exactly one image option (plus the placeholder), pre-select it
            if (
                this.selectedBackgroundImageIndex === "none" &&
                this.backgroundImages.length === 2
            ) {
                this.selectedBackgroundImageIndex =
                    this.backgroundImages[1].value;
            }

            this.$bvModal.show(MODAL_ID);
        },

        onClose() {
            this.$nextTick(() => {
                this.$bvModal.hide(MODAL_ID);
            });
        },

        async onModalOpen() {
            this.removeTextFromImage = true;
            this.updateTranslationSettings();
        },

        onModalShown() {
            const header = this.getModalHeaderElement();

            if (header) {
                this.removeModalHeaderDragListener();
                this.modalHeaderElement = header;
                header.addEventListener("mousedown", this.startModalDrag);
            }
        },

        async updateTranslationSettings() {
            try {
                const settings = await loadTranslationSettings();
                this.glossary = settings.glossary || [];
                this.exclude = settings.exclude || [];
            } catch (err) {
                console.error("Failed to load translation settings in modal", err);
            }
        },

        async onModalHide() {
            this.removeModalDragListeners();
            this.resetModalPosition();

            this.selectedBackgroundImageIndex = "none";
            this.showTranslationSettings = false;
            this.selectedImage = null;
        },

        onModalHidden() {
            if (!this.shouldOpenMakeEditableAfterHidden) {
                return;
            }

            this.shouldOpenMakeEditableAfterHidden = false;

            this.$nextTick(() => {
                const opened = openMakeTemplateImageEditableForInstance(this.instanceId);

                if (!opened) {
                    toastr.error("Could not open Make Editable for the translated image.");
                }
            });
        },

        currentTranslationKey() {
            if (!this.selectedImage) return null;
            return `${this.layoutId}::${this.selectedImage}`;
        },

        upsertStoredTranslation(key, language, patch) {
            if (!key) return;
            const list = this.translationsByKey[key]
                ? [...this.translationsByKey[key]]
                : [];
            const idx = list.findIndex((i) => i.language === language);
            if (idx === -1) {
                list.push({ language, url: null, isLoading: false, error: false, errorMessage: null, model: null, ...patch });
            } else {
                list[idx] = { ...list[idx], ...patch };
            }
            this.$set(this.translationsByKey, key, list);
        },

        rehydrateTranslations() {
            const key = this.currentTranslationKey();
            const stored = key ? this.translationsByKey[key] : null;
            this.translatedImageUrls = stored ? stored.map((i) => ({ ...i })) : [];
            this.syncEditedImageUrl();
        },

        syncEditedImageUrl() {
            this.editedImageUrl = this.translatedImageUrls.find((i) => i.url && !i.error)?.url || null;
        },

        cachedTextBlocks() {
            const key = this.currentTranslationKey();
            return key ? this.textBlocksByKey[key] : null;
        },

        persistTextBlocks(key) {
            if (!key) return;
            this.$set(this.textBlocksByKey, key, {
                raw: this.rawDetectedTextBlocks.map((b) => ({ ...b })),
                detected: [...this.detectedTextBlocks],
                selected: [...this.selectedTextBlocks],
            });
        },

        notifyTranslationDone(name) {
            axios
                .post("/image-translation/notify-done", {
                    layout_id: this.layoutId,
                    name: name || null,
                })
                .catch((e) => {
                    console.error("Failed to send image-translation notification", e);
                });
        },

        notifyTranslationStarted(name) {
            axios
                .post("/image-translation/notify-started", {
                    layout_id: this.layoutId,
                    name: name || null,
                })
                .catch((e) => {
                    console.error("Failed to send image-translation started notification", e);
                });
        },

        refreshImage() {
            if (!this.targetElement) {
                this.selectedImage = null;
                return;
            }

            // Check if the selected element is an image
            const isImage =
                this.targetElement.type === "image" ||
                (this.targetElement.id &&
                    (this.targetElement.id.includes("image_") ||
                        this.targetElement.id.includes("product_image")));

            if (isImage) {
                //ignore base64 images like tables
                if (this.targetElement?.getSrc().includes(";base64,")) {
                    this.selectedImage = null;
                    return;
                }

                // Get the original image source
                let originalSrc = deleteURLParameters(
                    this.targetElement?.getSrc(),
                    ["lightweight", "full_resolution"],
                );
                originalSrc = setURLParameters(originalSrc, {
                    full_resolution: true,
                });

                // Only reset if the image has actually changed
                if (this.selectedImage !== originalSrc) {
                    this.selectedImage = originalSrc;
                }

                // If it's the default image, treat it as if it's not an image
                if (this.isDefaultImage) {
                    this.selectedImage = null;
                }
            } else {
                this.selectedImage = null;
            }

            this.rehydrateTranslations();
        },

        async fetchTextLinesFromImage() {
            if (!this.selectedImage) return;

            this.isLoadingTextBlocks = true;
            this.rawDetectedTextBlocks = [];
            this.detectedTextBlocks = [];
            this.selectedTextBlocks = [];

            let imageUrl = this.selectedImage;
            if (imageUrl.startsWith("/")) {
                imageUrl = window.location.origin + imageUrl;
            }

            try {
                // Convert to base64 to ensure access
                const imageResponse = await fetch(imageUrl);
                const contentType = imageResponse.headers.get("content-type") || "";

                if (!imageResponse.ok) {
                    throw new Error(`Image fetch failed: ${imageResponse.status} ${imageResponse.statusText}`);
                }

                if (!contentType.startsWith("image/") && !contentType.includes("octet-stream")) {
                    console.warn(`Expected image response, got ${contentType || "unknown content type"}`);
                }

                let blob = await imageResponse.blob();

                // Convert all images to PNG to ensure compatibility with OpenAI Vision API 
                // which does not natively support AVIF files.
                blob = await this.convertImageToPng(blob);

                if (!blob.size) {
                    throw new Error("Image fetch returned an empty blob.");
                }
                
                const base64Promise = new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                
                const base64Image = await base64Promise;

                const exclusionRule = this.detectBrandText 
                    ? "EXTRACT ALL VISIBLE TEXT, INCLUDING text that appears physically printed on products, labels, packaging, or bottles." 
                    : "CRITICAL: Extract all text that belongs to the graphic design, including headings, captions, badges, callouts, annotations, and labels placed around a product. Always include annotation text connected by lines, arrows, or dots to parts of a product because it is overlay text, not text physically printed on the product. Exclude only text whose letters are visibly printed directly on the physical surface of a photographed product, package, bottle, device, label, or screen. Do not exclude surrounding graphic annotations merely because they describe or point to a physical object.";

                const extractionPrompt = `You are a highly precise text extraction system. Extract visible text blocks from the provided image according to the following rules:

1. ${exclusionRule}
2. Do NOT group text by product or layout unless they are clearly part of the same paragraph (having the exact same font, size, and styling).
3. Extract each distinct line or paragraph as a separate individual text block.

Return a JSON object with a single key 'blocks' containing an array of objects. Each object must have:
- 'text' (the string extracted)
- 'isLogoProductNameOrBrandName' (boolean, set to true ONLY if the text is a recognizable brand name, product name, or logo).`;

                const payload = {
                    model: "gpt-5.6-sol",
                    messages: [
                        {
                            role: "user",
                            content: [
                                {
                                    type: "text",
                                    text: extractionPrompt
                                },
                                {
                                    type: "image_url",
                                    image_url: {
                                        url: base64Image
                                    }
                                }
                            ]
                        }
                    ],
                    response_format: {
                        name: "extracted_blocks",
                        schema: {
                            type: "object",
                            properties: {
                                blocks: {
                                    type: "array",
                                    items: {
                                        type: "object",
                                        properties: {
                                            text: { type: "string" },
                                            isLogoProductNameOrBrandName: { type: "boolean" }
                                        },
                                        required: ["text", "isLogoProductNameOrBrandName"],
                                        additionalProperties: false
                                    }
                                }
                            },
                            required: ["blocks"],
                            additionalProperties: false
                        },
                        strict: true
                    }
                };

                const responseData = await this.callOpenAI(payload);

                let contentString = null;

                if (responseData && responseData.content) {
                    contentString = responseData.content;
                } else if (responseData && responseData.choices && responseData.choices.length > 0) {
                    contentString = responseData.choices[0].message.content;
                }

                if (contentString) {
                    try {
                        const contentJson = JSON.parse(contentString);
                        if (contentJson && Array.isArray(contentJson.blocks)) {
                            const filteredBlocks = contentJson.blocks.filter(block => block && block.text && block.text.length > 2);
                            
                            const uniqueBlocksMap = new Map();
                            filteredBlocks.forEach(b => {
                                if (!uniqueBlocksMap.has(b.text)) {
                                    uniqueBlocksMap.set(b.text, b);
                                }
                            });
                            
                            this.rawDetectedTextBlocks = Array.from(uniqueBlocksMap.values());
                            this.detectedTextBlocks = this.rawDetectedTextBlocks.map(b => b.text);

                            if (this.isSelectedCanvasProductImage) {
                                this.selectedTextBlocks = [];
                            } else {
                                if (this.detectBrandText) {
                                    this.selectedTextBlocks = [...this.detectedTextBlocks];
                                } else {
                                    this.selectedTextBlocks = this.rawDetectedTextBlocks
                                        .filter(b => !b.isLogoProductNameOrBrandName)
                                        .map(b => b.text);
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Error parsing content JSON:", e);
                    }
                }
            } catch (error) {
                console.error("Error fetching text blocks:", error);
                toastr.error("Failed to load text blocks.");
            } finally {
                this.isLoadingTextBlocks = false;
                if (this.rawDetectedTextBlocks.length) {
                    this.persistTextBlocks(this.currentTranslationKey());
                }
            }
        },

        clearSelectedTextBlocks() {
            this.selectedTextBlocks = [];
            this.showTextBlocksError = false;
        },

        isProductImageTextBlock() {
            return this.isSelectedCanvasProductImage;
        },

        clearSelectedTargetLanguages() {
            this.selectedTargetLanguages = [];
        },

        openMakeEditableAfterSave(shouldOpen) {
            if (!shouldOpen) {
                return;
            }

            this.shouldOpenMakeEditableAfterHidden = true;
        },

        getUniqueTerms(terms) {
            return [...new Set((terms || []).map(term => String(term).trim()).filter(Boolean))];
        },

        buildProtectedTermsRule(protectedTerms) {
            if (!protectedTerms.length) {
                return "";
            }

            return [
                "HARD RULE - PROTECTED TERMS:",
                `The following terms are protected and must remain EXACTLY as they appear in the original image: ${JSON.stringify(protectedTerms)}.`,
                "Do NOT translate, transliterate, paraphrase, rewrite, remove, replace, or visually edit any occurrence of these protected terms.",
                "Preserve protected terms with the original spelling, capitalization, punctuation, style, position, and pixels.",
                "If a protected term appears inside a larger text block, translate only the surrounding text and keep the protected term unchanged.",
                "",
            ].join(" ");
        },

        buildGlossaryRule(language) {
            const relevantGlossary = this.glossary.filter(g => g.language === language);
            if (!relevantGlossary.length) {
                return "";
            }

            const glossary = relevantGlossary.map(g => ({
                source: g.source,
                translation: g.translation,
            }));

            return `Use this glossary for non-protected terms: ${JSON.stringify(glossary)}. `;
        },

        async processTranslation(language, imageBlob, hasTransparency = false, translationKey = null, chromaKeyColor = null) {
            const currentIndex = this.translatedImageUrls.findIndex(i => i.language === language);
            let item = currentIndex !== -1
                ? this.translatedImageUrls[currentIndex]
                : { language, url: null, isLoading: true, error: false, errorMessage: null, model: this.selectedModel };

            // Set state to loading (only if this language is in the live view)
            if (currentIndex !== -1) {
                this.$set(this.translatedImageUrls, currentIndex, { ...item, isLoading: true, error: false, errorMessage: null });
            }
            this.upsertStoredTranslation(translationKey, language, { isLoading: true, error: false, errorMessage: null, model: this.selectedModel });

            try {
                const protectedTerms = this.getUniqueTerms(this.exclude);
                const protectedTermsRule = this.buildProtectedTermsRule(protectedTerms);
                const glossaryRule = this.buildGlossaryRule(language);

                // Simplified, focused prompt that explicitly preserves everything but text
                let prompt;
                const textBlocksToTranslate = this.selectedTextBlocks.length < this.detectedTextBlocks.length
                    ? this.selectedTextBlocks.map(block => String(block))
                    : null;

                // Create base prompt with critical preservation rules
                const basePreservationRules = `CRITICAL RULES:
1. ONLY translate the text. Change NOTHING else.
2. Keep ALL colors, backgrounds, gradients, shapes, borders exactly as they are.
3. Do NOT modify background colors, image backgrounds, or any visual elements.
4. Preserve exact font colors, positioning, spacing, and layout.
5. Keep all symbols (+, -, *, /, °, №, etc.) exactly as they appear.`;

                if (textBlocksToTranslate) {
                    // Specific text blocks mode
                    prompt = `${basePreservationRules}
6. Translate ONLY these text blocks to ${language}: ${JSON.stringify(textBlocksToTranslate)}
7. Do NOT translate or modify any text not in this list.
${protectedTermsRule}${glossaryRule}`;
                } else if (this.detectBrandText) {
                    // Translate all including brand names
                    prompt = `${basePreservationRules}
6. Translate all visible text to ${language}, including brand names and logos.
7. Do NOT translate text on product packaging, bottles, or labels shown in images.
${protectedTermsRule}${glossaryRule}`;
                } else {
                    // Translate non-brand text
                    prompt = `${basePreservationRules}
6. Translate all visible text to ${language} EXCEPT brand names, product names, and logos.
7. Do NOT translate text on product packaging, bottles, or labels shown in images.
${protectedTermsRule}${glossaryRule}`;
                }

                if (hasTransparency) {
                    prompt += `
8. PRESERVE background transparency and colors exactly.`;
                }

                const dimensions = await new Promise((resolve, reject) => {
                    const img = new Image();
                    const objectUrl = URL.createObjectURL(imageBlob);
                    img.onload = () => {
                        URL.revokeObjectURL(objectUrl);
                        resolve({ width: img.width, height: img.height });
                    };
                    img.onerror = (err) => {
                        URL.revokeObjectURL(objectUrl);
                        reject(err);
                    };
                    img.src = objectUrl;
                });
                const targetSize = `${dimensions.width}x${dimensions.height}`;

                const formData = new FormData();
                const file = new File([imageBlob], "input.png", { type: imageBlob.type });
                formData.append("image", file);
                formData.append("infillMethod", this.selectedModel);
                formData.append("overwritePrompt", prompt);
                formData.append("width", dimensions.width);
                formData.append("height", dimensions.height);
                formData.append("upscaleTranslatedImage", this.upscaleTranslatedImage ? "1" : "0");

                const generateRes = await axios.post(
                    "/ai-edit-image",
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                if (!generateRes?.data || generateRes.data.error || !generateRes.data.url) {
                    const generationError = generateRes?.data?.error;
                    const generationErrorMessage = typeof generationError === "string"
                        ? generationError
                        : generationError?.message;
                    throw new Error(generationErrorMessage || `Failed to translate to ${language}.`);
                }

                let finalUrl = generateRes.data.url;

                // Restore transparency by removing the chroma key color that was used
                // during preprocessing. This converts the chromaKey color back to transparency.
                if (hasTransparency && chromaKeyColor) {
                    const restoredBlob = await this.removeChromaKey(finalUrl, chromaKeyColor);
                    finalUrl = URL.createObjectURL(restoredBlob);
                }

                const finalIndex = this.translatedImageUrls.findIndex(i => i.language === language);
                if (finalIndex !== -1) {
                    this.$set(this.translatedImageUrls, finalIndex, { ...item, url: finalUrl, isLoading: false, error: false, errorMessage: null });
                }
                this.upsertStoredTranslation(translationKey, language, { url: finalUrl, isLoading: false, error: false, errorMessage: null });

                // Use first successful one as editedImageUrl for background removal compatibility
                if (!this.editedImageUrl) {
                    this.editedImageUrl = finalUrl;
                }
            } catch (e) {
                console.error(`Error translating to ${language}:`, e);
                toastr.error(`Error translating to ${language}.`);
                const responseError = e?.response?.data?.error;
                const responseErrorMessage = typeof responseError === "string"
                    ? responseError
                    : responseError?.message || e?.message || "";
                const errorMessage = item.model === "gpt-image-2" && responseErrorMessage.toLowerCase().includes("rejected by the safety system")
                    ? "This image couldn't be processed with GPT-Image-2. Try Gemini / Nano Banana instead."
                    : null;
                const errIndex = this.translatedImageUrls.findIndex(i => i.language === language);
                if (errIndex !== -1) {
                    this.$set(this.translatedImageUrls, errIndex, { ...item, isLoading: false, error: true, errorMessage });
                }
                this.upsertStoredTranslation(translationKey, language, { isLoading: false, error: true, errorMessage });
            }
        },

        async retryTranslation(index) {
            const item = this.translatedImageUrls[index];
            if (!item || item.isLoading) return;

             try {
                if (!this.selectedImage) {
                    toastr.error("No source image selected.");
                    return;
                }
                // Re-fetch blob for retry
                let imageUrl = this.selectedImage;
                 if (imageUrl.startsWith("/")) {
                     imageUrl = window.location.origin + imageUrl;
                 }
                const imageResponse = await fetch(imageUrl);
                const originalImageBlob = await imageResponse.blob();

                const { blob: imageBlob, hasTransparency, chromaKeyColor } = await this.applyChromaKey(originalImageBlob);

                await this.processTranslation(item.language, imageBlob, hasTransparency, this.currentTranslationKey(), chromaKeyColor);
             } catch (e) {
                console.error("Retry preparation failed:", e);
                toastr.error("Failed to prepare image for retry.");
             }
        },

        async handleTranslate() {
            if (this.isLoading) return;

            try {
                this.isLoading = true;
                this.showAlreadyGeneratedWarning = false;

                if (!this.selectedImage) {
                    toastr.error("No image selected.");
                    this.isLoading = false;
                    return;
                }

                if (!this.selectedTargetLanguages.length) {
                    toastr.error("No target language selected.");
                    this.isLoading = false;
                    return;
                }
                
                if (this.selectedTextBlocks.length === 0) {
                    this.showTextBlocksError = true;
                    this.isLoading = false;
                    return;
                }
                this.showTextBlocksError = false;

                // 1. Identify which selected languages are completely new vs already translated
                const selected = this.selectedTargetLanguages || [];
                
                let newLanguagesToTranslate = [];
                let languagesToRetranslate = [];
                let oldModelNameForAlert = "";

                selected.forEach(lang => {
                    const existing = (this.translatedImageUrls || []).find(item => item.language === lang);
                    if (!existing) {
                        newLanguagesToTranslate.push(lang);
                    } else if (existing.model && existing.model !== this.selectedModel) {
                        languagesToRetranslate.push(lang);
                        // Just pick the first different model we find for the alert text
                        const oldModelOpt = this.modelOptions.find(o => o.value === existing.model);
                        if (oldModelOpt && !oldModelNameForAlert) {
                            oldModelNameForAlert = oldModelOpt.text;
                        }
                    }
                });

                let finalLanguagesToTranslate = [...newLanguagesToTranslate];

                if (languagesToRetranslate.length > 0) {
                    const newModelOpt = this.modelOptions.find(o => o.value === this.selectedModel);
                    const rawNewModelName = newModelOpt ? newModelOpt.text : this.selectedModel;
                    
                    const cleanOldModelName = (oldModelNameForAlert || '').replace(/\s*\(.*?\)/g, '').trim();
                    const cleanNewModelName = (rawNewModelName || '').replace(/\s*\(.*?\)/g, '').trim();
                    
                    const isAll = (languagesToRetranslate.length === selected.length);
                    const quantityText = isAll ? 'All' : 'Some';
                    
                    const swalResult = await Swal.fire({
                        title: 'Retranslate Images?',
                        text: `${quantityText} of the selected languages have already been translated using ${cleanOldModelName || 'a different model'}. Do you want to replace them with a new translation made with ${cleanNewModelName}?`,
                        icon: 'question',
                        showDenyButton: !isAll,
                        showCancelButton: true,
                        confirmButtonText: 'Retranslate all',
                        denyButtonText: 'Translate only missing',
                        cancelButtonText: 'Cancel',
                        confirmButtonColor: 'rgb(32, 156, 134)'
                    });

                    if (swalResult.isDismissed && swalResult.dismiss === Swal.DismissReason.cancel) {
                        this.isLoading = false;
                        return;
                    }

                    if (swalResult.isConfirmed) {
                        // User wants to retranslate
                        finalLanguagesToTranslate = [...finalLanguagesToTranslate, ...languagesToRetranslate];
                    }
                }

                if (finalLanguagesToTranslate.length === 0) {
                    this.showAlreadyGeneratedWarning = true;
                    this.isLoading = false;
                    return;
                }

                const displayName = this.projectName || this.instanceTemplateName || "";
                this.notifyTranslationStarted(displayName);
                toastr.info(`Starting translation for ${finalLanguagesToTranslate.length} language(s)...`);

                // Update translatedImageUrls array
                // For existing ones that are being retranslated, we reset their state.
                // For new ones, we append them.
                const itemsToProcess = [];
                
                finalLanguagesToTranslate.forEach(lang => {
                    const existingIndex = this.translatedImageUrls.findIndex(item => item.language === lang);
                    if (existingIndex !== -1) {
                        this.translatedImageUrls[existingIndex].url = null;
                        this.translatedImageUrls[existingIndex].isLoading = true;
                        this.translatedImageUrls[existingIndex].error = false;
                        this.translatedImageUrls[existingIndex].errorMessage = null;
                        this.translatedImageUrls[existingIndex].model = this.selectedModel;
                        itemsToProcess.push({ index: existingIndex, language: lang });
                    } else {
                        const newIndex = this.translatedImageUrls.length;
                        this.translatedImageUrls.push({
                            language: lang,
                            url: null,
                            isLoading: true,
                            error: false,
                            errorMessage: null,
                            model: this.selectedModel
                        });
                        itemsToProcess.push({ index: newIndex, language: lang });
                    }
                });
                this.syncEditedImageUrl();

                // Persist the in-progress state so it survives closing/reopening the popup
                // (POR-13666, Part 3), keyed by the current layout + image.
                const translationKey = this.currentTranslationKey();
                itemsToProcess.forEach(({ language }) => {
                    this.upsertStoredTranslation(translationKey, language, {
                        url: null, isLoading: true, error: false, errorMessage: null, model: this.selectedModel,
                    });
                });
                // Cache the detected/selected text blocks so reopening the popup shows them
                // without re-running the "Detecting text blocks…" step.
                this.persistTextBlocks(translationKey);

                // Get blob once
                let imageUrl = this.selectedImage;
                if (imageUrl.startsWith("/")) {
                    imageUrl = window.location.origin + imageUrl;
                }
                const originalImageBlob = await fetch(imageUrl).then(r => r.blob());

                const { blob: imageBlob, hasTransparency, chromaKeyColor } = await this.applyChromaKey(originalImageBlob);

                // Process items in parallel
                await Promise.all(
                    itemsToProcess.map(item => this.processTranslation(item.language, imageBlob, hasTransparency, translationKey, chromaKeyColor))
                );

                // Notify the bell once the whole batch is done (POR-13666, Part 2).
                // Fire-and-forget: the promises above resolve even if the popup was closed,
                // and the store is authoritative regardless of the live view.
                const finishedItems = this.translationsByKey[translationKey] || [];
                if (finishedItems.some(i => i.url && !i.error)) {
                    this.notifyTranslationDone(displayName);
                }

            } catch (err) {
                console.error("handleTranslate error:", err);
                toastr.error("Service temporarily unavailable.");
            } finally {
                this.isLoading = false;
            }
        },

        async handleRemoveBackground() {
            if (!this.editedImageUrl) {
                toastr.error("No image to process");
                return;
            }

            this.isRemovingBackground = true;

            try {
                let filePath;

                // Check if it's already a /share?file= URL - extract the path
                if (this.editedImageUrl.includes("/share?file=")) {
                    try {
                        const url = new URL(this.editedImageUrl, window.location.origin);
                        filePath = url.searchParams.get("file");
                    } catch {
                        // Fall through to upload
                    }
                }

                // If we couldn't extract a path, upload the image first
                if (!filePath) {
                    const response = await fetch(this.editedImageUrl);
                    const blob = await response.blob();

                    const now = new Date();
                    const timestamp = now.toISOString().replace(/[:.]/g, "-");
                    const filename = `temp-${timestamp}.png`;
                    const file = new File([blob], filename, { type: "image/png" });

                    // Upload using the background image endpoint (same as GenAI popup)
                    const currentPreview = this.selectedInstance;
                    const customerId = $("#template-group-preview").data("customer-id");
                    const themeId = currentPreview?.data?.theme || $("#template_selector").val();

                    const formData = new FormData();
                    formData.append("template_id", currentPreview?.template?.id);
                    formData.append("theme_id", themeId);
                    formData.append("customer_id", customerId);
                    formData.append("file_1", file);
                    formData.append("filename_1", file.name);

                    const uploadResponse = await axios.post("/banner/upload_bk_image", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });

                    const uploaded = uploadResponse.data?.[0];
                    if (!uploaded?.path) {
                        throw new Error("Failed to upload image for background removal");
                    }
                    filePath = uploaded.path;
                }

                // Now call background removal endpoint
                const currentPreview = this.selectedInstance;
                const customerId = $("#template-group-preview").data("customer-id");
                const companyId = document.getElementById("company_selector")?.value || 0;

                const payload = {
                    filePath,
                    template_id: currentPreview?.template?.id,
                    customer_id: customerId,
                    company_id: companyId,
                    is_crop_to_image: false,
                };

                const bgRemoveResponse = await axios.post("/file/remove-img-background", payload);

                if (!bgRemoveResponse.data?.imgUrl) {
                    throw new Error("Background removal did not return image URL");
                }

                // Update the edited image URL with the background-removed version
                const returnedUrl = bgRemoveResponse.data.imgUrl;
                this.editedImageUrl = returnedUrl.includes("/share?file=")
                    ? returnedUrl
                    : `/share?file=${returnedUrl}`;

                toastr.success("Background removed successfully");
            } catch (error) {
                console.error("Error removing background:", error);
                toastr.error("Failed to remove background: " + (error.message || "Unknown error"));
            } finally {
                this.isRemovingBackground = false;
            }
        },

        async handleSave(openMakeEditableAfterSave = false) {
            this.selectedInstance.canvas.setActiveObject(this.targetElement);
            this.selectedInstance.canvas.renderAll();

            if (!this.editedImageUrl) {
                alert("No changes to save. Please remove text first.");
                return;
            }

            if (!this.layoutId) {
                alert("Failed to get layout id.");
                return;
            }

            // Handle Product Images differently
            if (this.isProductImage) {
                await this.handleSaveProductImage();
                return;
            }

            // Handle Background/Static Images
            await this.handleSaveBackgroundImage(openMakeEditableAfterSave);
        },

        async handleSaveProductImage() {
            $(".loadingOverlay").fadeIn();
            $(".loadingOverlay-text").html("Uploading product image...");

            try {
                const response = await fetch(this.editedImageUrl);
                const blob = await response.blob();

                const now = new Date();
                const timestamp = now.toISOString().replace(/[:.]/g, "-");
                const filename = `translated-${timestamp}.png`;
                const file = new File([blob], filename, { type: "image/png" });

                // Upload the image to the server
                const formData = new FormData();
                formData.append("image_type", "product_image");
                formData.append("company_id", "-1");
                formData.append("background_remove", false);
                formData.append("crop_to_image", false);
                formData.append("is_convert", true);
                formData.append("convert", "png");
                formData.append("upload_images[]", file);

                $(".loadingOverlay-text").html("Processing product image...");

                const uploadResponse = await axios.post("/file/uploadimg/store", formData);

                if (uploadResponse.data && uploadResponse.data.filenames && uploadResponse.data.filenames.length > 0) {
                    const uploadedFilename = uploadResponse.data.filenames[0];
                    // Remove extension to get productId
                    const productId = uploadedFilename.replace(/\.[^/.]+$/, "");

                    $(".loadingOverlay-text").html("Replacing product image...");

                    // Set up a one-time listener to close the "move to folder" modal when it opens
                    const $addProductImagesModal = $("#addProductImages");
                    $addProductImagesModal.one("shown.bs.modal", () => {
                        $addProductImagesModal.modal("hide");
                    });
                    
                    // Ensure the target element is the active object so the event listener doesn't abort
                    if (this.targetElement && this.selectedInstance && this.selectedInstance.canvas) {
                        this.selectedInstance.canvas.setActiveObject(this.targetElement);
                        this.selectedInstance.canvas.renderAll();
                    }

                    // Fire the event to replace the product image
                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                        action: "productImage",
                        value: {
                            type: "uploaded_product_image",
                            productIds: productId,
                        },
                    });

                    // Wait for the replacement to complete
                    setTimeout(() => {
                        $(".loadingOverlay").fadeOut();
                        this.$bvModal.hide(MODAL_ID);
                        toastr.success("Product image replaced successfully");
                    }, 500);
                } else {
                    throw new Error("Failed to upload image");
                }
            } catch (error) {
                console.error("Error saving product image:", error);
                $(".loadingOverlay").fadeOut();
                toastr.error("Failed to save product image: " + (error.message || "Unknown error"));
            }
        },

        async handleSaveBackgroundImage(openMakeEditableAfterSave = false) {
            $(".loadingOverlay").fadeIn();
            $(".loadingOverlay-text").html("Saving edited image...");
            $("#selectBkImgModal").css("visibility", "hidden");

            // Get or create folder
            let folderId = this.folderId;
            if (!folderId) {
                const folder = await this.checkLinkedFolderExists();
                if (folder) {
                    folderId = folder.id;
                } else {
                    folderId = await this.createFolder(
                        "Layout_" + this.layoutId,
                    );
                }
                this.folderId = folderId;
            }

            try {
                const fileData = await this.saveImageToLayoutsFolder(this.editedImageUrl);
                             if (fileData && fileData.path) {
                    const newImageUrl = `/share?file=${fileData.path}`;
                    
                    if (this.targetElement && this.targetElement.id) {
                        // Ensure the target element is the active object
                        if (this.selectedInstance && this.selectedInstance.canvas) {
                            this.selectedInstance.canvas.setActiveObject(this.targetElement);
                            this.selectedInstance.canvas.renderAll();
                        }
                        
                        // Use the canonical event to replace background image (identical to ImageEditorControl)
                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                            action: 'removeBackgroundThemeImgBG',
                            value: {
                                imgUrl: newImageUrl,
                                objectId: this.targetElement.id,
                                instanceId: this.instanceId,
                                target: this.targetElement,
                                isFromDropdown: true
                            }
                        });
                        
                        setTimeout(() => {
                            $(".loadingOverlay").fadeOut();
                            $("#selectBkImgModal").css("visibility", "visible");
                            this.openMakeEditableAfterSave(openMakeEditableAfterSave);
                            this.$bvModal.hide(MODAL_ID);
                        }, 500);
                    } else {
                         toastr.warning("Image saved but might not update on canvas immediately. Please reload if necessary.");
                         $(".loadingOverlay").fadeOut();
                         this.openMakeEditableAfterSave(openMakeEditableAfterSave);
                         this.$bvModal.hide(MODAL_ID);
                    }
                } else {
                    throw new Error("Failed to get file path from upload response");
                }
            } catch (error) {
                console.error("Error saving background image:", error);
                $(".loadingOverlay").fadeOut();
                toastr.error("Failed to save background image: " + (error.message || "Unknown error"));
                $("#selectBkImgModal").css("visibility", "visible");
            }
        },

        async saveImageToLayoutsFolder(imageUrl) {
            try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
                const file = new File([blob], `image-${timestamp}.png`, { type: 'image/png' });

                const uploadResponse = await uploadFolderFile([file], this.folderId);
                const fileData = uploadResponse[0];

                if (fileData && !fileData.isExist) {
                    const { uploadId, key, data, uploadedFolder, fileName: uploadedFileName, thumbnail, id } = fileData;

                    const signedUrlResponse = await axios.get(`/file/upload_folder_file/${uploadId}/1?key=${key}`);
                    const { url } = signedUrlResponse.data;

                    if (url) {
                        await uploadFile(url, file);

                        const partsResponse = await axios.get(`/file/upload_folder_file/${uploadId}?key=${key}`);

                        if (partsResponse.data.parts) {
                            const completeResponse = await axios.post(`/file/upload_folder_file/${uploadId}/complete`, {
                                data,
                                fileName: uploadedFileName,
                                id,
                                isExist: false,
                                key,
                                uploadedFolder,
                                parts: partsResponse.data.parts,
                                thumbnail,
                            });
                             if (completeResponse.data && completeResponse.data.user_file) {
                                return completeResponse.data.user_file;
                            }
                        }
                    }
                }
                return fileData;
            } catch (error) {
                console.error('Error saving to Layouts folder:', error);
                throw error;
            }
        },
        async checkLinkedFolderExists() {
            try {
                const response = await Requests.checkLinkedFolderExists(
                    "/file/check_linked_folder_exists",
                    {
                        layout_id: this.layoutId,
                    },
                );

                return response.data.success ? response.data.folder : null;
            } catch (error) {
                console.error("Error checking linked folder:", error);
                return null;
            }
        },
        async createFolder(folderName) {
            try {
                const response = await Requests.createFolderByName(
                    "/file/create_folder_by_name",
                    {
                        parent_id: null,
                        layout_id: this.layoutId,
                        name: folderName,
                    },
                );

                return response.data.id;
            } catch (error) {
                console.error("Error creating folder:", error);
                throw new Error("Failed to create folder");
            }
        },

        async handleAddToNewTemplate(widthOrOption, height = null) {
            if (!this.editedImageUrl) {
                toastr.error("No translated image available");
                return;
            }

            // Handle 'original' size option
            if (widthOrOption === "original") {
                const currentTemplate = this.selectedInstance?.template;
                if (!currentTemplate) {
                    toastr.error("Could not get current template dimensions");
                    return;
                }
                const templateWidth = parseInt(currentTemplate.width);
                const templateHeight = parseInt(currentTemplate.height);
                if (!templateWidth || !templateHeight) {
                    toastr.error("Could not get valid template dimensions");
                    return;
                }
                await this.createTemplateWithSize(templateWidth, templateHeight);
                return;
            }

            // Handle 'custom' size option
            if (widthOrOption === "custom") {
                const result = await this.showCustomSizeDialog();
                if (result.isConfirmed && result.value) {
                    await this.createTemplateWithSize(result.value.width, result.value.height);
                }
                return;
            }

            // Handle predefined sizes
            if (typeof widthOrOption === "number" && typeof height === "number") {
                await this.createTemplateWithSize(widthOrOption, height);
            }
        },

        showCustomSizeDialog() {
            return Swal.fire({
                title: "Enter custom size",
                html:
                    '<div class="form-group">' +
                    '<label for="custom-width">Width (px)</label>' +
                    '<input id="custom-width" type="number" class="form-control" value="2000" min="100" max="10000">' +
                    "</div>" +
                    '<div class="form-group">' +
                    '<label for="custom-height">Height (px)</label>' +
                    '<input id="custom-height" type="number" class="form-control" value="2000" min="100" max="10000">' +
                    "</div>",
                showCancelButton: true,
                confirmButtonText: "Create",
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    const width = document.getElementById("custom-width").value;
                    const height = document.getElementById("custom-height").value;

                    if (!width || !height || width < 100 || height < 100 || width > 10000 || height > 10000) {
                        Swal.showValidationMessage("Please enter valid dimensions (100-10000px)");
                        return false;
                    }

                    return {
                        width: parseInt(width),
                        height: parseInt(height),
                    };
                },
            });
        },

        async createTemplateWithSize(width, height) {
            this.isCreatingTemplate = true;

            try {
                const grid = $("#template-group-preview");
                const customerId = grid.attr("data-customer-id");

                if (this.hasMultipleLanguages) {
                  await this.waitForTranslatedImagesReady();
                }

                // Determine which images to process
                // For multiple languages, use all translated images
                // For single language, use just the edited image
                const imagesToProcess = this.hasMultipleLanguages
                  ? this.translatedImageUrls.filter(item => item.url && !item.isLoading && !item.error)
                  : [this.editedImageUrl];

                let templatesCreated = 0;

                for (let i = 0; i < imagesToProcess.length; i++) {
                    let imageItem = imagesToProcess[i];
                    let imageUrl = typeof imageItem === "object" && imageItem.url ? imageItem.url : imageItem;
                    const imageLang = this.selectedTargetLanguages[i];

                    const languageLabel = this.hasMultipleLanguages
                        ? ` (${imageLang || `Language ${i + 1}`})`
                        : "";


                    // Convert the image URL to a blob if it's an HTTP URL or blob URL
                    if (imageUrl.startsWith("blob:")) {
                        let folderId = this.folderId;
                        if (!folderId) {
                            const folder = await this.checkLinkedFolderExists();
                            folderId = folder ? folder.id : await this.createFolder("Layout_" + this.layoutId);
                            this.folderId = folderId;
                        }
                        const fileData = await this.saveImageToLayoutsFolder(imageUrl);
                        if (fileData && fileData.path) {
                            imageUrl = `/share?file=${fileData.path}`;
                        } else {
                            throw new Error("Failed to upload blob for template creation");
                        }
                    } else if (imageUrl.startsWith("http")) {
                        const formData = new FormData();
                        formData.append("img_url", imageUrl);
                        formData.append("return_blob", 1);

                        const response = await axios({
                            method: "post",
                            url: `/banner/${customerId}/group/${this.layoutId}/template/${this.instanceId}/upload_generated_background`,
                            data: formData,
                        });
                        imageUrl = response.data;
                    }

                    // Create a new template with the selected size
                    // Use 'fit' mode to ensure the image fits within the template bounds
                    let result;
                    try {
                        result = await API_TEMPLATE.createTemplateWithSize(
                            customerId,
                            this.layoutId,
                            imageUrl,
                            width,
                            height,
                            [],
                            "fit",
                            imageLang,
                            null,
                            this.projectName
                        );
                    } catch (error) {
                        if (error.response && error.response.status === 422) {
                            await new Promise(resolve => setTimeout(resolve, 3000));
                            result = await API_TEMPLATE.createTemplateWithSize(
                                customerId,
                                this.layoutId,
                                imageUrl,
                                width,
                                height,
                                [],
                                "fit",
                                imageLang,
                                null,
                                this.projectName
                            );
                        } else {
                            throw error;
                        }
                    }

                    if (result.data && result.data.success) {
                        templatesCreated++;
                        if (this.hasMultipleLanguages) {
                            toastr.info(`Template created${languageLabel} (${templatesCreated}/${imagesToProcess.length})`);
                        }
                    } else {
                        console.error(`Failed to create template${languageLabel}`);
                    }
                }

                if (templatesCreated > 0) {
                    toastr.success(`${templatesCreated} template(s) created successfully`);
                    // Reload the page to show the new template(s)
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                } else {
                    throw new Error("Failed to create any templates");
                }
            } catch (error) {
                console.error("Error creating template:", error);
                toastr.error("Failed to create template: " + (error.message || "Unknown error"));
            } finally {
                this.isCreatingTemplate = false;
            }
        },

        async waitForTranslatedImagesReady() {
          const TIMEOUT = 180000; // 180 seconds

          return new Promise((resolve, reject) => {
            let isFinished = false;
            let pollTimeoutId = null;
            let rejectTimeoutId = null;

            const finishResolve = () => {
              if (isFinished) {
                return;
              }

              isFinished = true;
              clearTimeout(pollTimeoutId);
              clearTimeout(rejectTimeoutId);
              resolve();
            };

            const finishReject = () => {
              if (isFinished) {
                return;
              }

              isFinished = true;
              clearTimeout(pollTimeoutId);
              clearTimeout(rejectTimeoutId);
              reject(new Error("Timed out waiting for translated images"));
            };

            const check = () => {
              if (isFinished) {
                return;
              }

              if (!this.isAnyImageProcessing()) {
                finishResolve();
                return;
              }

              pollTimeoutId = setTimeout(check, 300);
            };

            rejectTimeoutId = setTimeout(finishReject, TIMEOUT);
            check();
          });
        },

        isAnyImageProcessing() {
          return (this.translatedImageUrls || []).some(item =>
              item.isLoading || (!item.url && !item.error)
          );
        },

         sanitizeFileName(value, fallback = '') {
          if (!value) return fallback;

           return value.replace(/[^A-Za-z0-9]+/g, '_');
        },

        async downloadImage(imageUrl, label = "image", options = {}) {
          if (options && options.format === "jpg") {
            return this.downloadImageAsJpg(imageUrl, label, options);
          }

          try {
            this.isDownloading = true;
            const response = await fetch(imageUrl);
            let blob = await response.blob();
  
            if (options && options.format === "png") {
              blob = await this.convertImageToPng(blob);
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
  
            const safeLabel = this.sanitizeFileName(label, 'image');
            
            const langResolveResponse = await axios.post('/language/resolve', {
              label: label
            });

            const resolvedLangCode = langResolveResponse.data.code || safeLabel;

            const baseName = this.projectName
                ? this.sanitizeFileName(this.projectName)
                : this.getDownloadTimestamp();

            a.href = url;
            a.download = `${baseName}_${resolvedLangCode}.png`;

            document.body.appendChild(a);
            a.click();
  
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isDownloading = false;
          } catch (error) {
            this.isDownloading = false;
            console.error("Download failed:", error);
            toastr.error("Failed to download image");
          }
        },

      async downloadAllImagesZip(options = {}) {
        const images = this.getDownloadableTranslatedImages();

        if (images.length === 0) {
          toastr.error("No images to download");
          return;
        }

        this.isDownloadingAllImages = true;

        const format = options.format || "png";
        const maxSizeKB = Number(options.maxSizeKB || 1500);

        try {
          toastr.info(`Starting download for ${images.length} image(s)...`);

          const resolvedCodes = await this.resolveImageLanguageCodes(images);
          const zip = await this.buildImagesZip(images, resolvedCodes, { format, maxSizeKB });
          const zipBaseName = this.getZipBaseName();

          await this.downloadZipFile(zip, `${zipBaseName}_translated_images.zip`);

          toastr.success(`Downloaded ${images.length} images as ZIP`);
        } catch (error) {
          console.error("ZIP download failed:", error);
          toastr.error("Failed to download images ZIP");
        } finally {
          this.isDownloadingAllImages = false;
        }
      },

      getDownloadableTranslatedImages() {
        return this.translatedImageUrls
          .filter(item => item.url)
          .map(item => ({
            imageUrl: item.url,
            label: item.language
          }));
      },

      async resolveImageLanguageCodes(images) {
        const labels = [...new Set(images.map(image => image.label).filter(Boolean))];

        const langResolveResponse = await axios.post("/language/resolve-many", {
          labels
        });

        return langResolveResponse.data.codes || {};
      },

      async buildImagesZip(images, resolvedCodes, { format = "png", maxSizeKB = 1500 } = {}) {
        const zip = new JSZip();
        const baseName = this.projectName
          ? this.sanitizeFileName(this.projectName)
          : this.getDownloadTimestamp();
        const extension = format === "jpg" ? "jpg" : "png";

        for (const image of images) {
          const sourceBlob = await this.fetchImageBlob(image.imageUrl);
          const outputBlob = format === "jpg"
            ? await this.compressImageToJpg(sourceBlob, maxSizeKB)
            : await this.convertImageToPng(sourceBlob);

          const resolvedLangCode = this.getResolvedImageLangCode(image.label, resolvedCodes);

          zip.file(`${baseName}_${resolvedLangCode}.${extension}`, outputBlob);
        }

        return zip;
      },
      async convertImageToPng(blob) {
        if (blob.type === "image/png") return blob;
        const imageBitmap = await createImageBitmap(blob);
        const canvas = document.createElement("canvas");
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imageBitmap, 0, 0);
        // Use toDataURL then convert to blob for better Chrome compatibility
        return new Promise((resolve) => {
            const dataUrl = canvas.toDataURL("image/png");
            fetch(dataUrl)
                .then(res => res.blob())
                .then(blob => resolve(blob))
                .catch(() => {
                    // Fallback to toBlob if fetch fails
                    canvas.toBlob(resolve, "image/png");
                });
        });
      },
      async compressImageToJpg(blob, maxSizeKB = 1500) {
        const imageBitmap = await createImageBitmap(blob);
        const canvas = document.createElement("canvas");
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(imageBitmap, 0, 0);

        const MAX_SIZE_BYTES = maxSizeKB * 1024;
        let quality = 0.92;
        let jpgBlob;

        do {
          jpgBlob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", quality)
          );
          quality = parseFloat((quality - 0.05).toFixed(2));
        } while (jpgBlob.size > MAX_SIZE_BYTES && quality > 0.1);

        return jpgBlob;
      },

      async fetchImageBlob(imageUrl) {
        const response = await fetch(imageUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${imageUrl}`);
        }

        return await response.blob();
      },

      getResolvedImageLangCode(label, resolvedCodes) {
        const safeLabel = label ? label.replace(/\s+/g, "_") : "image";

        return resolvedCodes[label] || safeLabel;
      },

      getDownloadTimestamp() {
        return new Date().toISOString().replace(/[:.]/g, "-");
      },

      getZipBaseName() {
        return this.projectName
          ? this.sanitizeFileName(this.projectName)
          : this.getDownloadTimestamp()
      },

      async downloadZipFile(zip, fileName) {
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipUrl = window.URL.createObjectURL(zipBlob);
        const a = document.createElement("a");

        a.href = zipUrl;
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(zipUrl);
      },
      getModalElement() {
          return document.getElementById(MODAL_ID);
      },
      getModalHeaderElement() {
          return this.getModalElement()?.querySelector(".draggable-modal-header") || null;
      },
      getModalDialogElement() {
          return this.getModalElement()?.querySelector(".modal-dialog") || null;
      },
      startModalDrag(event) {
          if (event.button !== 0 || !(event.target instanceof Element)) {
              return;
          }

          if (
              event.target.closest("button") ||
              event.target.closest(".dropdown") ||
              event.target.closest(".translate-settings-gear")
          ) {
              return;
          }

          const dialog = this.getModalDialogElement();

          if (!dialog) {
              return;
          }

          this.modalDialogElement = dialog;

          this.isDraggingModal = true;
          this.modalDragStartX = event.clientX;
          this.modalDragStartY = event.clientY;
          this.modalStartTranslateX = this.modalTranslateX;
          this.modalStartTranslateY = this.modalTranslateY;
          dialog.classList.add("is-dragging");

          document.addEventListener("mousemove", this.handleModalDrag);
          document.addEventListener("mouseup", this.stopModalDrag);
          event.preventDefault();
      },
      handleModalDrag(event) {
          if (!this.isDraggingModal || !this.modalDialogElement) {
              return;
          }

            this.modalTranslateX = this.modalStartTranslateX + event.clientX - this.modalDragStartX;
            this.modalTranslateY = this.modalStartTranslateY + event.clientY - this.modalDragStartY;

            this.modalDialogElement.style.transform = `translate(${this.modalTranslateX}px, ${this.modalTranslateY}px)`;
      },
      stopModalDrag() {
          document.removeEventListener("mousemove", this.handleModalDrag);
          document.removeEventListener("mouseup", this.stopModalDrag);

          this.modalDialogElement?.classList.remove("is-dragging");
          this.isDraggingModal = false;
          this.modalDialogElement = null;
      },

      resetModalPosition() {
          const dialog = this.modalDialogElement || this.getModalDialogElement();

          if (!dialog) {
              return;
          }

          dialog.style.position = "";
          dialog.style.margin = "";
          dialog.style.width = "";
          dialog.style.left = "";
          dialog.style.top = "";
          dialog.style.transform = "";
          this.modalTranslateX = 0;
          this.modalTranslateY = 0;
          this.modalStartTranslateX = 0;
          this.modalStartTranslateY = 0;
      },
      removeModalHeaderDragListener() {
          if (this.modalHeaderElement) {
              this.modalHeaderElement.removeEventListener("mousedown", this.startModalDrag);
              this.modalHeaderElement = null;
          }
      },
      removeModalDragListeners() {
          this.removeModalHeaderDragListener();

          document.removeEventListener("mousemove", this.handleModalDrag);
          document.removeEventListener("mouseup", this.stopModalDrag);

          this.modalDialogElement?.classList.remove("is-dragging");
          this.isDraggingModal = false;
          this.modalDialogElement = null;
          this.modalStartTranslateX = this.modalTranslateX;
          this.modalStartTranslateY = this.modalTranslateY;
      },
      toggleDownloadOptions(index) {
        this.activeOriginalDownloadOptions = false; // close original if open
        this.activeDownloadAllOptions = false;
        this.activeDownloadOptionsIndex =
          this.activeDownloadOptionsIndex === index ? null : index;
      },
      toggleOriginalDownloadOptions() {
        this.activeDownloadOptionsIndex = null;
        this.activeDownloadAllOptions = false;
        this.activeOriginalDownloadOptions = !this.activeOriginalDownloadOptions;
      },
      closeDownloadOptions() {
        this.activeDownloadOptionsIndex = null;
        this.activeOriginalDownloadOptions = false;
        this.activeDownloadAllOptions = false;
      },
      async downloadImageAsJpg(imageUrl, label = "image", options = {}) {
        try {
          this.isDownloading = true;
          const maxSizeKB = Number(options.maxSizeKB || 1500);
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const safeLabel = this.sanitizeFileName(label, 'image');
          const langResolveResponse = await axios.post('/language/resolve', { label });
          const resolvedLangCode = langResolveResponse.data.code || safeLabel;
          const baseName = this.projectName
            ? this.sanitizeFileName(this.projectName)
            : this.getDownloadTimestamp();

          let jpgBlob = await this.compressImageToJpg(blob, maxSizeKB);

          const url = window.URL.createObjectURL(jpgBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${baseName}_${resolvedLangCode}.jpg`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.isDownloading = false;
        } catch (error) {
          this.isDownloading = false;
          console.error("Download failed:", error);
          toastr.error("Failed to download image");
        }
      },
      handleDownloadAllOptions() {
        this.activeDownloadOptionsIndex = null;
        this.activeOriginalDownloadOptions = false;
        this.activeDownloadAllOptions = !this.activeDownloadAllOptions;
      },
      applyChromaKey(imageBlob) {
          return new Promise((resolve, reject) => {
              const img = new Image();
              const objectUrl = URL.createObjectURL(imageBlob);
              img.onload = () => {
                  URL.revokeObjectURL(objectUrl);
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d', { willReadFrequently: true });
                  ctx.drawImage(img, 0, 0);
                  
                  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                  const data = imageData.data;
                  let hasTransparency = false;
                  
                  // First, detect which color to use for chroma key
                  const chromaKeyColor = this.selectOptimalChromaKeyColor(imageData);
                  
                  for (let i = 0; i < data.length; i += 4) {
                      const alpha = data[i + 3];
                      if (alpha < 255) {
                          hasTransparency = true;
                          // Blend with selected chroma key color
                          const alphaNorm = alpha / 255;
                          data[i] = Math.round(data[i] * alphaNorm + chromaKeyColor.r * (1 - alphaNorm)); // R
                          data[i + 1] = Math.round(data[i + 1] * alphaNorm + chromaKeyColor.g * (1 - alphaNorm)); // G
                          data[i + 2] = Math.round(data[i + 2] * alphaNorm + chromaKeyColor.b * (1 - alphaNorm)); // B
                          data[i + 3] = 255; // Fully opaque
                      }
                  }
                  
                  if (hasTransparency) {
                      ctx.putImageData(imageData, 0, 0);
                      // Use toDataURL then convert to blob for better Chrome compatibility
                      // This avoids Chrome's automatic color space conversions
                      const dataUrl = canvas.toDataURL('image/png');
                      fetch(dataUrl)
                          .then(res => res.blob())
                          .then(blob => {
                              resolve({ blob, hasTransparency: true, chromaKeyColor });
                          })
                          .catch(() => {
                              // Fallback to toBlob if fetch fails
                              canvas.toBlob((blob) => {
                                  resolve({ blob, hasTransparency: true, chromaKeyColor });
                              }, 'image/png');
                          });
                  } else {
                      resolve({ blob: imageBlob, hasTransparency: false, chromaKeyColor });
                  }
              };
              img.onerror = reject;
              img.src = objectUrl;
          });
      },
      
      selectOptimalChromaKeyColor(imageData) {
          // List of 5 very different colors to try for chroma key
          const candidateColors = [
              { r: 0, g: 255, b: 0, name: "Green" },        // Bright Green
              { r: 255, g: 0, b: 255, name: "Magenta" },     // Bright Magenta
              { r: 0, g: 255, b: 255, name: "Cyan" },        // Bright Cyan
              { r: 255, g: 255, b: 0, name: "Yellow" },      // Bright Yellow
              { r: 255, g: 0, b: 0, name: "Red" }            // Bright Red
          ];
          
          // Count pixels close to each candidate color
          const data = imageData.data;
          const colorUsageCounts = candidateColors.map(() => 0);
          
          for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const alpha = data[i + 3];
              
              // Count all pixels with at least some opacity
              if (alpha > 0) {
                  for (let j = 0; j < candidateColors.length; j++) {
                      const candidate = candidateColors[j];
                      const dist = Math.sqrt(
                          Math.pow(r - candidate.r, 2) + 
                          Math.pow(g - candidate.g, 2) + 
                          Math.pow(b - candidate.b, 2)
                      );
                      
                      // If pixel is close to this candidate color, increment count
                      // Using larger tolerance to detect significant usage of the color
                      if (dist < 120) {
                          colorUsageCounts[j]++;
                      }
                  }
              }
          }
          
          // Find the color with the lowest usage
          let minIndex = 0;
          let minCount = colorUsageCounts[0];
          for (let i = 1; i < colorUsageCounts.length; i++) {
              if (colorUsageCounts[i] < minCount) {
                  minCount = colorUsageCounts[i];
                  minIndex = i;
              }
          }
          
          const selectedColor = candidateColors[minIndex];
          const totalPixels = data.length / 4;
          const usagePercentage = ((minCount / totalPixels) * 100).toFixed(2);
          
          console.log(`=== CHROMA KEY COLOR DETECTION ===`);
          console.log(`Total pixels analyzed: ${totalPixels}`);
          console.log(`Color usage breakdown:`);
          candidateColors.forEach((c, idx) => {
              const pct = ((colorUsageCounts[idx] / totalPixels) * 100).toFixed(2);
              console.log(`  ${c.name} (${c.r},${c.g},${c.b}): ${colorUsageCounts[idx]} pixels (${pct}%)`);
          });
          console.log(`\nSelected: ${selectedColor.name} RGB(${selectedColor.r},${selectedColor.g},${selectedColor.b}) - ${minCount} pixels (${usagePercentage}%)`);
          console.log(`================================`);
          
          return selectedColor;
      },
      removeChromaKey(imageUrl, chromaKeyColor) {
          return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = "Anonymous";
              img.onload = () => {
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d', { willReadFrequently: true });
                  ctx.drawImage(img, 0, 0);
                  
                  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                  const data = imageData.data;
                  
                  // Use the color that was used for chroma key
                  const targetR = chromaKeyColor.r;
                  const targetG = chromaKeyColor.g;
                  const targetB = chromaKeyColor.b;
                  
                  console.log(`=== REMOVING CHROMA KEY ===`);
                  console.log(`Target color to remove: ${chromaKeyColor.name} RGB(${targetR},${targetG},${targetB})`);
                  
                  let pixelsRemoved = 0;
                  let pixelsEdgeProcessed = 0;
                  
                  for (let i = 0; i < data.length; i += 4) {
                      const r = data[i];
                      const g = data[i + 1];
                      const b = data[i + 2];
                      
                      // Euclidean distance to the chroma key color
                      const dist = Math.sqrt(
                          Math.pow(r - targetR, 2) + 
                          Math.pow(g - targetG, 2) + 
                          Math.pow(b - targetB, 2)
                      );
                      
                      // VERY STRICT: Only remove pixels that are extremely close to the chroma key color
                      // Reduced tolerance to prevent removing unrelated pixels
                      if (dist < 40) { 
                          // Core chroma key - make fully transparent
                          data[i + 3] = 0;
                          pixelsRemoved++;
                      } else if (dist < 60) {
                          // Edge pixels - gradually fade based on distance
                          // This handles anti-aliasing without modifying colors
                          const fadeAmount = (dist - 40) / 20; // 0 to 1
                          data[i + 3] = Math.round(255 * (1 - fadeAmount));
                          pixelsEdgeProcessed++;
                      }
                      // Do NOT apply any color correction - only modify alpha channel
                      // This prevents unintended color changes
                  }
                  
                  console.log(`Pixels removed: ${pixelsRemoved}`);
                  console.log(`Edge pixels faded: ${pixelsEdgeProcessed}`);
                  console.log(`========================`);
                  
                  ctx.putImageData(imageData, 0, 0);
                  // Use toDataURL then convert to blob for better Chrome compatibility
                  // This avoids Chrome's automatic color space conversions
                  const dataUrl = canvas.toDataURL('image/png');
                  fetch(dataUrl)
                      .then(res => res.blob())
                      .then(blob => {
                          resolve(blob);
                      })
                      .catch(() => {
                          // Fallback to toBlob if fetch fails
                          canvas.toBlob((blob) => {
                              resolve(blob);
                          }, 'image/png');
                      });
              };
              img.onerror = reject;
              img.src = imageUrl;
          });
      },
    },
};
</script>

<style scoped lang="scss">
::v-deep .multiselect {
  &__tag {
  border-radius: 9999px;
    &-icon {
      &::after {
        color: #fff;
      }
    }
  }
}

::v-deep .modal.translate-template-image-modal .translate-settings-gear,
::v-deep .translate-template-image-modal .translate-settings-gear {
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  line-height: 1;
}

::v-deep .modal.translate-template-image-modal .translate-settings-gear i,
::v-deep .translate-template-image-modal .translate-settings-gear i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  line-height: 1;
  font-size: 16px;
  margin: 0;
  padding: 0;
}

::v-deep .translate-advanced-menu {
  min-width: 320px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
}

.translate-advanced-menu__title {
  margin-bottom: 12px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
}

.language-actions {
  gap: 12px;
}

.glossary-settings-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.glossary-settings-button__icon {
  flex-shrink: 0;
}

.text-removal-footer {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    justify-content: flex-end;
    position: sticky;
    bottom: 0;
    background-color: #fff;
}

.language-select {
    cursor: pointer;
}

.upscale-image-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    z-index: 1070;
    width: 280px;
    padding: 6px 8px;
    border-radius: 4px;
    background: #191919;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.35;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s ease;
}

.upscale-image-tooltip::after {
    position: absolute;
    top: 100%;
    left: 18px;
    border: 5px solid transparent;
    border-top-color: #191919;
    content: "";
}

.custom-control-label:hover .upscale-image-tooltip {
    opacity: 1;
    visibility: visible;
}

.clear-selection-button {
    color: #64748b;
    font-size: 12px;
    line-height: 1.2;
    text-decoration: none;

    &:hover,
    &:focus {
        color: #343a40;
        text-decoration: none;
    }

    &:disabled {
        color: #adb5bd;
        cursor: default;
    }
}

.text-block-product-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: #e9ecef;
    color: #495057;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
}

.modal-content-wrapper {
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 4px;
  backdrop-filter: blur(2px);
}

.loading-overlay .text-white {
    color: #333 !important; /* Override since background is light */
}

.image-select-dropdown {
    width: 100%;
    position: relative;

    ::v-deep {
        .btn {
            width: 100%;
            cursor: pointer;
            text-align: left;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: #fff;
            border: 1px solid #ced4da;
            color: #495057;

            &:hover {
                background-color: #fff;
                border-color: #b1bbc4;
            }

            &:focus {
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
            }
        }

        .dropdown-menu {
            width: 100%;
            min-width: 100%;
            max-width: 100%;
            top: calc(100% + 2px) !important;
            left: 0 !important;
            right: auto !important;
            transform: none !important;
        }
    }
}

.image-thumbnail {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
}

.image-thumbnail-placeholder {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
}

.modal-preview-image {
    max-height: 280px;
    max-width: 100%;
    display: block;
}

.image-preview-wrapper {
    position: relative;
    display: inline-block;
}

/* Container for overlay buttons */
.image-actions-container {
    display: none;
    position: absolute;
    top: 10px;
    right: 10px;
    gap: 8px; /* space between buttons */
    z-index: 1;
    flex-direction: row; /* Force row direction */
    align-items: center;
}

/* Show container on wrapper hover */
.image-preview-wrapper:hover .image-actions-container {
    display: flex;
}

.download-btn,
.retry-btn {
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    line-height: 35px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    padding: 0; 
    margin: 0; 
    position: static !important; /* Force static positioning inside flex container */
    text-align: center;
}

.download-btn:hover,
.retry-btn:hover {
    background: rgba(0, 0, 0, 0.8);
}

.download-btn i,
.retry-btn i {
    font-size: 1.2rem;
    line-height: 1; 
    display: inline-block;
}

.translate-running-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 14px 0;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(32, 156, 134, 0.08);
  border: 1px solid rgba(32, 156, 134, 0.25);
  color: #157a68;
  font-size: 13.5px;
  line-height: 1.4;

  &__icon {
    font-size: 17px;
    line-height: 1.35;
    color: #209c86;
    flex-shrink: 0;
  }
}

.translated-images-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;

  .image-preview-wrapper {
    width: 100%;
  }

  .modal-preview-image {
    max-height: 200px;
    width: 100%;
    object-fit: contain;
  }
}

.translated-image-item {
    text-align: left;
}

.translated-image-label {
  display: block;
  margin-bottom: 5px;
}

.translate-template-image-modal .draggable-modal-header {
    cursor: move;
    user-select: none;
}

.translate-template-image-modal .draggable-modal-header button,
.translate-template-image-modal .draggable-modal-header .close {
    cursor: pointer;
}

.translate-template-image-modal .modal-dialog.is-dragging {
    transition: none !important;
    animation: none !important;
    will-change: transform;
}

.image-dropdown-menu {
    max-height: 300px;
    overflow-y: auto;

    .dropdown-item {
        cursor: pointer;
        padding: 8px 16px;
        color: #212529;

        &:hover,
        &:focus {
            background-color: #f8f9fa;
            color: #212529;
        }

        &.active {
            background-color: #e9ecef;
            color: #212529;
        }
    }
}

.multiselect-language {
    min-height: 38px;
    cursor: pointer;
}
.text-muted-foreground {
  color: #64748b;
}
.image-preview-wrapper {
  position: relative;
}

.custom-download-dropdown {
  position: absolute;
  top: 45px;
  right: 10px;
  width: 255px;
  background: #fff;
  border-radius: 6px;
  padding: 14px 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
  border: 1px solid #e5e7eb;
}

.custom-download-dropdown--download-all {
  top: auto;
  right: auto;
  bottom: calc(100% + 8px);
  left: 0;
}

.download-label,
.download-unit {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.download-size-input {
  height: 32px;
  max-width: 150px;
  font-size: 13px;
}

::v-deep .custom-radio {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  cursor: pointer;
  color: #111827;

  input {
    display: none;
  }

  span {
    width: 15px;
    height: 15px;
    border: 1px solid #14b8a6;
    border-radius: 50%;
    margin-right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  input:checked + span::after {
    content: "";
    width: 9px;
    height: 9px;
    background: #14b8a6;
    border-radius: 50%;
  }
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
